import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Settings, Key, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { ChatInterface } from '@/components/chat/ChatInterface'
import { useChatWorkflow } from '@/hooks/use-chat-workflow'
import { useAuth } from '@/hooks/useAuth'
import { LiveBrowserViewer } from '@/components/anchor-browser/LiveBrowserViewer'

export function ChatWorkflowBuilder() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  useToast() // Toast hook for future use
  useAuth() // Authentication hook for future use
  
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [isConnecting, setIsConnecting] = useState(false)
  
  const {
    messages,
    isLoading,
    isConnected,
    currentSession,
    sendMessage,
    executeCode,
    executeCodeViaCDP,
    initializeMCP
  } = useChatWorkflow()

  const isEditing = Boolean(id)

  useEffect(() => {
    // Auto-connect to MCP service (API key is handled server-side)
    const embeddedApiKey = 'sk-0068a536869992ce88fe7dfc5568ce66'
    setApiKey(embeddedApiKey)
    handleConnectMCP()
  }, [])

  const handleConnectMCP = async () => {
    setIsConnecting(true)
    try {
      const connected = await initializeMCP()
      if (connected) {
        setShowApiKeyDialog(false)
      }
    } catch (error) {
      console.error('Failed to connect to MCP:', error)
    } finally {
      setIsConnecting(false)
    }
  }





  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="border-b p-4 flex items-center justify-between min-h-[80px]">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/workflows')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold truncate">
              {isEditing ? 'Edit Workflow' : 'AI Workflow Generator'}
            </h1>
            <p className="text-muted-foreground text-sm truncate">
              Describe your automation task and get Playwright code
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {isConnected && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              Connected to MCP
            </div>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowApiKeyDialog(true)}
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Chat Interface - Responsive width for code blocks and buttons */}
        <div className="w-full lg:w-[600px] xl:w-[700px] p-4 lg:border-r">
          {isConnected ? (
            <ChatInterface
              messages={messages}
              isLoading={isLoading}
              onSendMessage={sendMessage}
              onExecuteCode={executeCode}
              onExecuteCodeViaCDP={executeCodeViaCDP}
              currentSessionId={currentSession.sessionId || undefined}
              className="h-full"
            />
          ) : (
            <Card className="h-full flex items-center justify-center">
              <CardContent className="text-center">
                {isConnecting ? (
                  <>
                    <Loader2 className="h-12 w-12 mx-auto mb-4 text-primary animate-spin" />
                    <h3 className="text-lg font-semibold mb-2">Connecting to Anchor Browser MCP</h3>
                    <p className="text-muted-foreground">
                      Establishing connection to generate Playwright code...
                    </p>
                  </>
                ) : (
                  <>
                    <Key className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-semibold mb-2">Connection Failed</h3>
                    <p className="text-muted-foreground mb-4">
                      Unable to connect to Anchor Browser MCP service
                    </p>
                    <Button onClick={handleConnectMCP}>
                      Retry Connection
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Live Browser Viewer - Larger */}
        <div className="flex-1 p-4">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                Live Browser Session
                {currentSession.sessionId && (
                  <Badge variant="secondary" className="text-xs">
                    {currentSession.sessionId}
                  </Badge>
                )}
                {currentSession.status === 'active' && (
                  <div className="flex items-center gap-1 text-green-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-xs">Live</span>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 p-0">
              {currentSession.liveUrl ? (
                <div className="h-full">
                  <LiveBrowserViewer
                    liveUrl={currentSession.liveUrl}
                    taskId={currentSession.sessionId!}
                    taskStatus={currentSession.status}
                    className="h-full rounded-lg overflow-hidden"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-muted-foreground">
                    <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-lg flex items-center justify-center">
                      <div className="w-8 h-8 bg-muted-foreground/20 rounded" />
                    </div>
                    <p className="text-sm">Execute Playwright code to start browser session</p>
                    <p className="text-xs mt-1">Live browser view will appear here</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* API Key Configuration Dialog */}
      <Dialog open={showApiKeyDialog} onOpenChange={setShowApiKeyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Anchor Browser MCP Connection</DialogTitle>
            <DialogDescription>
              The application uses an embedded Anchor Browser API key to connect to the MCP service.
              Connection status and settings are shown below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="apiKey">API Key (Embedded)</Label>
              <Input
                id="apiKey"
                type="password"
                value={apiKey}
                readOnly
                className="mt-1 bg-muted"
              />
              <p className="text-xs text-muted-foreground mt-1">
                API key is embedded in the application for seamless experience
              </p>
            </div>

            {isConnected ? (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                Successfully connected to MCP service
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <div className="w-2 h-2 bg-red-500 rounded-full" />
                Connection failed - check network connectivity
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApiKeyDialog(false)}>
              Close
            </Button>
            {!isConnected && (
              <Button
                onClick={handleConnectMCP}
                disabled={isConnecting}
              >
                {isConnecting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  'Retry Connection'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
